import { intl, ui } from '@beelday/common'
import { useSelector } from 'app-redux'
import { Config } from 'common/config'
import { getWordCloudApi } from 'features/pollings/word-cloud/word-cloud-api'
import { selectMyWordCount } from 'features/pollings/word-cloud/word-cloud-redux'
import { useEffect, useState } from 'react'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import { ConfiguredArchivedActivity } from './configured-archived-activity'
import { WordCloudView } from './presentation/poll-views'
import { Confirmation, StateActions } from './presentation/primitives'
import { useConfirmedSend } from './use-confirmed-send'

/** The same cap the desktop input applies. */
const MAX_LENGTH = 25

/**
 * A word-cloud poll on the phone: one short phrase, then a confirmation. The
 * cloud itself grows on the projector.
 *
 * "Have I answered" is my own entries in `answers` (`selectMyWordCount`).
 */
export const ConfiguredWordCloud = (): JSX.Element | null => {
	const roomAddress = useAssertedJoinedRoomAddress()
	const me = useAssertedWorkflowUser()
	const poll = useSelector(state => state.wordCloud.pollingDefinition)
	const mine = useSelector(state => selectMyWordCount(state, me.id))
	const { status, send, reset } = useConfirmedSend(mine)
	const [word, setWord] = useState('')
	const [addingAnother, setAddingAnother] = useState(false)

	// Cleared only once the answer is in the room's state; a failed send
	// keeps the word for another try.
	useEffect(() => {
		if (status === 'confirmed') {
			setWord('')
		}
	}, [status])

	if (!poll) {
		return null
	}

	if (poll.doneInPreviousSession) {
		return (
			<ConfiguredArchivedActivity
				toolType="POLLING_OPEN_ENDED_WORD_CLOUD"
				title={poll.question}
			/>
		)
	}

	const answered = mine > 0 && status !== 'sending' && status !== 'failed'

	if (answered && !addingAnother) {
		return (
			<Confirmation
				title={<intl.Translate>in_person.mobile.sent.title</intl.Translate>}
				description={
					<intl.Translate>in_person.mobile.sent.poll</intl.Translate>
				}
				testId="in_person.mobile.poll.sent"
			>
				{poll.allowForMultipleAnswer ? (
					<StateActions>
						<ui.LinkButton
							type="button"
							onClick={() => {
								reset()
								setAddingAnother(true)
							}}
						>
							<intl.Translate>in_person.mobile.poll.add_another</intl.Translate>
						</ui.LinkButton>
					</StateActions>
				) : null}
			</Confirmation>
		)
	}

	return (
		<WordCloudView
			question={poll.question}
			value={word}
			maxLength={MAX_LENGTH}
			sending={status === 'sending'}
			failed={status === 'failed'}
			focusOnMount={addingAnother}
			onChange={setWord}
			onSend={() => {
				const text = word.trim()
				if (!text) {
					return
				}
				setAddingAnother(false)
				send(() =>
					getWordCloudApi(Config.beeldayBackendUrl).sendAnswer(
						roomAddress,
						poll.id,
						text
					)
				)
			}}
		/>
	)
}

export default ConfiguredWordCloud
